import { isUitkHeaderLevelValid, isUitkHeaderSizeValid } from 'models/common/common-types';
import { PropertySearchListingViewModel } from 'models/hotel/search-types';
import { ResultType } from 'src/common/constants/resultType';

import { CrossSellProductType, LodgingCrossSellProductType, LodgingPwaPropertySearch, MessageType } from '__generated__/api';
import { mapMessageModel } from './search-results-adapter';

const PROPERTY_KEY_PREFIX = 'resultListing';
const MESSAGE_KEY_PREFIX = 'resultMessageListing';
const STATIC_MULTI_ITEM_CROSS_SELL_KEY_PREFIX = 'staticMultiItemCrossSell';
const STATIC_LODGING_HEADING_KEY_PREFIX = 'lodgingHeading';
const PROPERTY_DATA_STID = 'property-listing';

export const mapPropertySearchListings = (query: LodgingPwaPropertySearch.Query): PropertySearchListingViewModel[] => {
  const { propertySearchListings } = query.propertySearch;

  if (!propertySearchListings) {
    return null;
  }

  return propertySearchListings
    .map((propertySearchListing, index) => {
      switch (propertySearchListing.__typename) {
        case 'Property':
          return adaptProperty(propertySearchListing as LodgingPwaPropertySearch.PropertyInlineFragment, index);
        case 'MessageResult':
          return adaptMessage(propertySearchListing as LodgingPwaPropertySearch.MessageResultInlineFragment, index);
        case 'LodgingHeading':
          return adaptLodgingHeading(propertySearchListing as LodgingPwaPropertySearch.LodgingHeadingInlineFragment, index);
        case 'StaticLodgingMultiItemCrossSell':
          return adaptMultiItemLodgingStaticCrossSell(
            propertySearchListing as LodgingPwaPropertySearch.StaticLodgingMultiItemCrossSellInlineFragment,
            index
          );
        default:
          return null;
      }
    })
    .filter(notNull);
};

const notNull = <T>(obj: T) => obj !== null;

const adaptProperty = (property: LodgingPwaPropertySearch.PropertyInlineFragment, index: number) => {
  const id = Number.parseInt(property.id, 10);
  const resultType = ResultType.HOTEL;
  const key = id ? `${PROPERTY_KEY_PREFIX}-${id}-${index}` : `${PROPERTY_KEY_PREFIX}-${index}`;
  const dataStid = PROPERTY_DATA_STID;

  return { key, model: property, resultType, dataStid };
};

const adaptMessage = (message: LodgingPwaPropertySearch.MessageResultInlineFragment, index: number) => {
  const model = mapMessageModel(message, 'MessageResult');
  const key = `${MESSAGE_KEY_PREFIX}-${index}`;
  let resultType;

  switch (message.type) {
    case MessageType.FREE_CANCELLATION:
      resultType = ResultType.FREE_CANCELLATION;
      break;
    default:
      resultType = ResultType.MESSAGING;
      break;
  }

  return { key, model, resultType };
};

const adaptLodgingHeading = ({ value, size, level }: LodgingPwaPropertySearch.LodgingHeadingInlineFragment, index: number) => {
  if (isUitkHeaderLevelValid(level) && isUitkHeaderSizeValid(size)) {
    return {
      key: `${STATIC_LODGING_HEADING_KEY_PREFIX}-${index}`,
      resultType: ResultType.LIST_TITLE,
      model: {
        value,
        size,
        level,
      },
    };
  }

  return null;
};

const adaptMultiItemLodgingStaticCrossSell = (
  { crossSellProductType, fallbackListing }: LodgingPwaPropertySearch.StaticLodgingMultiItemCrossSellInlineFragment,
  index: number
) => ({
  key: `${STATIC_MULTI_ITEM_CROSS_SELL_KEY_PREFIX}-${index}`,
  resultType: ResultType.STATIC_LODGING_MULTI_ITEM_CROSS_SELL,
  model: {
    crossSellProductType: toCrossSellProductType(crossSellProductType),
    fallbackListingViewModel: fallbackListing ? adaptFallbackListing(fallbackListing) : null,
  },
});

const adaptFallbackListing = (fallbackListing: LodgingPwaPropertySearch.FallbackListing) => {
  switch (fallbackListing.__typename) {
    case 'MessageResult':
      return mapMessageModel(fallbackListing, 'MessageResult');
    default:
      return null;
  }
};

const toCrossSellProductType = (crossSellProductType: LodgingCrossSellProductType): CrossSellProductType | undefined => {
  switch (crossSellProductType) {
    case LodgingCrossSellProductType.CAR:
      return CrossSellProductType.CAR;
    default:
      return undefined;
  }
};
